import { useState } from 'react';
import { Button } from '@/components/ui/button';
import MemberSelectTable from 'components/IncidentConfig/MemberSelectTable';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import {
  useAddUsersToIncidentRoster,
  useGetRostersForGroup,
  useUpdatePositionAssignment,
} from 'hooks/incident/useRoster';

export type PositionDesignation = 'primary' | 'secondary';

type Slot = {
  designation: PositionDesignation;
  label: string;
};

const SLOTS: Slot[] = [
  { designation: 'primary', label: 'PRIMARY' },
  { designation: 'secondary', label: 'SECONDARY' },
];

function designationOnPosition(
  member: RosterEntry,
  positionId: string
): string | null {
  return (
    member.incidentPositions?.find((seat) => seat.positionId === positionId)
      ?.designation ?? null
  );
}

export function membersWithoutLeaderSlots(
  members: RosterEntry[],
  positionId: string
): RosterEntry[] {
  return members.filter((member) => {
    const designation = designationOnPosition(member, positionId);
    return designation !== 'primary' && designation !== 'secondary';
  });
}

type PositionLeaderSlotsProps = {
  incidentId: string;
  groupId: string;
  positionId: string;
  positionName: string;
  members: RosterEntry[];
  rostersForGroupQuery: ReturnType<typeof useGetRostersForGroup>;
  isDraft: boolean;
};

export function PositionLeaderSlots({
  incidentId,
  groupId,
  positionId,
  positionName,
  members,
  rostersForGroupQuery,
  isDraft,
}: PositionLeaderSlotsProps) {
  const [picking, setPicking] = useState<PositionDesignation | null>(null);
  const addUsers = useAddUsersToIncidentRoster(incidentId, groupId);
  const updateAssignment = useUpdatePositionAssignment();

  const occupant = (designation: PositionDesignation) =>
    members.find(
      (member) => designationOnPosition(member, positionId) === designation
    );

  const assign = (designation: PositionDesignation, userGuid: string) => {
    const current = occupant(designation);
    if (current?.userId === userGuid) {
      setPicking(null);
      return;
    }
    if (current) {
      updateAssignment.mutate({
        member: { user_guid: current.userId },
        incidentMemberId: current.incidentMemberId,
        positionId,
        designation: null,
        isDraft,
      });
    }
    const alreadyOnSeat = members.find((member) => member.userId === userGuid);
    if (alreadyOnSeat) {
      updateAssignment.mutate({
        member: { user_guid: alreadyOnSeat.userId },
        incidentMemberId: alreadyOnSeat.incidentMemberId,
        positionId,
        designation,
        isDraft,
      });
    } else {
      addUsers.mutate({
        positionAssignments: [
          {
            positionId,
            positionName,
            incidentMemberUserId: userGuid,
            isLeader: designation === 'primary',
            designation,
          },
        ],
        isDraft,
      });
    }
    setPicking(null);
  };

  const clearSlot = (designation: PositionDesignation) => {
    const current = occupant(designation);
    if (!current) return;
    updateAssignment.mutate({
      member: { user_guid: current.userId },
      incidentMemberId: current.incidentMemberId,
      positionId,
      designation: null,
      isDraft,
    });
  };

  return (
    <div className="tw:space-y-3">
      <div className="tw:text-xs tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase">
        On roster now
      </div>
      <div className="tw:grid tw:gap-3 tw:md:grid-cols-2">
        {SLOTS.map((slot) => {
          const member = occupant(slot.designation);
          return (
            <div
              key={slot.designation}
              className="tw:rounded-md tw:border tw:border-[#2a4568] tw:bg-[#1a2f4d] tw:p-3 tw:text-white"
            >
              <div className="tw:text-[11px] tw:font-semibold tw:tracking-wide tw:text-white/70 tw:uppercase">
                {positionName} - {slot.label}
              </div>
              {member ? (
                <>
                  <div className="tw:mt-2 tw:text-sm tw:font-medium tw:text-white">
                    {member.userName || member.emailAddress || 'Assigned'}
                  </div>
                  <div className="tw:mt-3 tw:flex tw:gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="tw:border-white/40 tw:bg-transparent tw:text-white tw:hover:bg-white/10 tw:hover:text-white"
                      onClick={() => setPicking(slot.designation)}
                    >
                      Change
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="tw:text-white/80 tw:hover:bg-white/10 tw:hover:text-white"
                      onClick={() => clearSlot(slot.designation)}
                    >
                      Clear
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="tw:mt-2 tw:text-sm tw:font-medium tw:text-white">
                    Unassigned
                  </div>
                  <div className="tw:mt-1 tw:text-xs tw:text-red-300">Gap</div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="tw:mt-3 tw:border-white/40 tw:bg-transparent tw:text-white tw:hover:bg-white/10 tw:hover:text-white"
                    onClick={() => setPicking(slot.designation)}
                  >
                    Assign
                  </Button>
                </>
              )}
            </div>
          );
        })}
      </div>
      {picking ? (
        <MemberSelectTable
          groupRoster={rostersForGroupQuery}
          currentPositionMembers={members}
          selectingLeader={picking === 'primary'}
          onSelectMembers={(selected) => {
            const [first] = selected;
            if (first) assign(picking, first.user_guid);
            else setPicking(null);
          }}
          closeTable={() => setPicking(null)}
        />
      ) : null}
    </div>
  );
}
